/**
 * The param names a route path declares, derived from the path literal itself.
 *
 * `"/project/:projectId/estimate/:estimateId"` -> `"projectId" | "estimateId"`.
 * A trailing `?` is stripped, so `":settingType?"` yields `"settingType"`.
 */
type Seg<P extends string> = P extends `${infer A}/${infer B}` ? Seg<A> | Seg<B> : P;
type StripOptional<S extends string> = S extends `${infer N}?` ? N : S;
type ParamOfSegment<S extends string> = S extends `:${infer N}` ? StripOptional<N> : never;
export type ParamsOfPaths<Paths extends readonly string[]> = ParamOfSegment<Seg<Paths[number]>>;

/**
 * What a URL param is allowed to hold. Every param gets one — there is no "unchecked" option, so a
 * new param cannot silently skip validation.
 *
 * - `objectId`       24-hex Mongo id.
 * - `uuid`           uuid v4, e.g. an escrow `batchId`, which is minted as `uuid.v4()` at upload.
 * - `slug`           a non-id token: a settings tab, a module name, a share token. Only checked for
 *                    being present and non-empty, because there is no format to check.
 */
export type ParamRule = "objectId" | "uuid" | "slug";

type RouteCommon = {
  /**
   * The route's component. Left open here and pinned per entry by {@link defineRoute}'s `Element`
   * parameter, so each route keeps its own component type instead of the array collapsing them to a
   * common one — their prop types are mutually incompatible.
   */
  element: unknown;
  exact?: boolean;
  showStickySidebar?: boolean;
  showHeader?: boolean;
  props?: Record<string, unknown>;
  /**
   * Query-string keys this route reads (`?versionId=…`), each with its rule. Checked only WHEN
   * PRESENT — a query param is optional by nature — so absence never blocks.
   */
  searchParams?: Readonly<Record<string, ParamRule>>;
  /**
   * This entry's path without `:projectId` is the portal-wide list, which its page shows for `?byPortal=true`.
   * Opened with neither `?projectId` nor `?byPortal=true`, RouteParamGuard adds `byPortal=true` (routeCanonical.ts):
   * without it the page waits for a project sidebar that never mounts, and renders nothing.
   */
  portalList?: true;
};

/**
 * A route entry. `params` must classify EVERY param the path declares — the mapped type is keyed on
 * the names derived from the path, so a missing entry, a typo and an invented key are all compile
 * errors rather than a param that silently goes unchecked at runtime.
 */
export type RouteDefinition<Paths extends readonly string[]> = [ParamsOfPaths<Paths>] extends [never]
  ? RouteCommon & { path: Paths; params?: never }
  : RouteCommon & { path: Paths; params: Readonly<Record<ParamsOfPaths<Paths>, ParamRule>> };

/**
 * Declares one route. The `const` type parameter keeps each `path` a literal, which is what lets
 * {@link RouteDefinition} read the `:param` names out of it.
 */
export const defineRoute = <const Paths extends readonly string[], Element>(
  route: RouteDefinition<Paths> & { element: Element },
) => route;
